import React, { useMemo, useRef, useState } from 'react';
import { Box, FormControlLabel, IconButton, Menu, MenuItem, Switch, Tooltip } from '@mui/material';

export default function ButtonTooltipTable({ table, ...rest }) {
	const {
		getAllColumns,
		getCenterLeafColumns,
		getLeftLeafColumns,
		getRightLeafColumns,
		getState,
		options: {
			icons: { ViewColumnIcon },
			localization,
		},
	} = table;

	const menuItemRef = useRef(null);

	const [anchorEl, setAnchorEl] = useState(null);
	const { density, columnOrder, columnPinning } = getState();

	const allColumns = useMemo(() => {
		const columns = getAllColumns();
		if (
			columnOrder.length > 0 &&
			!columns.some((col) => col.columnDef.columnDefType === 'group')
		) {
			return [
				...getLeftLeafColumns(),
				...Array.from(new Set(columnOrder)).map((colId) => {
					if (!['mrt-row-expand', 'mrt-row-spacer'].includes(colId)) {
						return getCenterLeafColumns().find((col) => col?.id === colId);
					}
				}),
				...getRightLeafColumns(),
			].filter(Boolean);
		}
		return columns;
	}, [
		columnOrder,
		columnPinning,
		getAllColumns(),
		getCenterLeafColumns(),
		getLeftLeafColumns(),
		getRightLeafColumns(),
	]);

	const handleClick = (event) => {
		setAnchorEl(event.currentTarget);
	};

	// console.log(allColumns)

	return (
		<>
			<Tooltip arrow title={rest?.title ?? localization.showHideColumns}>
				<IconButton
					aria-label={localization.showHideColumns}
					onClick={handleClick}
					{...rest}
					title={undefined}
				>
					<ViewColumnIcon />
				</IconButton>
			</Tooltip>
			{anchorEl && (
				<Menu
					anchorEl={anchorEl}
					open={!!anchorEl}
					onClose={() => setAnchorEl(null)}
					MenuListProps={{
						dense: density === 'compact',
					}}
				>
					{allColumns.map((column, index) => {
						const { columnDef } = column;
						const { columnDefType } = columnDef;

						const switchChecked =
							(columnDefType !== 'group' && column.getIsVisible()) ||
							(columnDefType === 'group' &&
								column.getLeafColumns().some((col) => col.getIsVisible()));

						const handleToggleColumnHidden = (columns) => {
							if (columnDefType === 'group') {
								column?.columns?.forEach?.((childColumn) => {
									childColumn.toggleVisibility(!switchChecked);
								});
							} else {
								column.toggleVisibility();
							}
						};

						return (
							<MenuItem
								key={`${index}-${column.id}`}
								disableRipple
								ref={menuItemRef}
								sx={(theme) => ({
									alignItems: 'center',
									justifyContent: 'flex-start',
									my: 0,
									opacity: 1,
									outlineOffset: '-2px',
									outline: 'none',
									pl: `${(column.depth + 0.5) * 2}rem`,
									py: '6px',
								})}
							>
								<Box
									sx={{
										display: 'flex',
										flexWrap: 'nowrap',
										gap: '8px',
									}}
								>
									<FormControlLabel
										componentsProps={{
											typography: {
												sx: {
													mb: 0,
													opacity: columnDefType !== 'display' ? 1 : 0.5,
												},
											},
										}}
										checked={switchChecked}
										control={
											<Tooltip
												arrow
												enterDelay={1000}
												enterNextDelay={1000}
												title={localization.toggleVisibility}
											>
												<Switch />
											</Tooltip>
										}
										disabled={!column.getCanHide()}
										label={columnDef.header}
										onChange={() => handleToggleColumnHidden(column)}
									/>
								</Box>
							</MenuItem>
						)
					})}
				</Menu>
			)}
		</>
	);
}